import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { translate } from "@/utils/translations";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Search,
  RefreshCw,
  ExternalLink,
  CheckCircle,
  XCircle,
  Clock,
  Hash,
  FileCheck,
  Zap,
  Shield,
  Copy,
  Play
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Proofs = () => {
  const location = useLocation();
  const { language } = useLanguage();
  const [proofs, setProofs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedProof, setSelectedProof] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  
  const t = (text) => translate(text, language);

  const isDemo = new URLSearchParams(location.search).get("demo") === "true";

  useEffect(() => {
    fetchProofs();
  }, [isDemo, activeTab]);

  const fetchProofs = async () => {
    setLoading(true);
    try {
      const typeParam = activeTab !== "all" ? `&proof_type=${activeTab}` : "";
      const res = await fetch(`${API}/proofs${isDemo ? '?demo=true' : '?'}${typeParam}`, {
        credentials: "include"
      });
      const data = await res.json();
      setProofs(data);
    } catch (e) {
      console.error("Failed to fetch proofs:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const truncateHash = (hash, len = 12) => {
    if (!hash) return "";
    return `${hash.slice(0, len)}...${hash.slice(-8)}`;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString();
  };

  const getProofTypeIcon = (type) => {
    switch (type) {
      case "inference": return Zap;
      case "action": return FileCheck;
      case "zk_receipt": return Shield;
      default: return Hash;
    }
  };

  const getProofTypeColor = (type) => {
    switch (type) {
      case "inference": return "bg-[#00D2FF]/10 text-[#00D2FF] border-[#00D2FF]/20";
      case "action": return "bg-[#00FF94]/10 text-[#00FF94] border-[#00FF94]/20";
      case "zk_receipt": return "bg-[#3399FF]/10 text-[#3399FF] border-[#3399FF]/20";
      default: return "bg-gray-500/10 text-gray-400 border-gray-500/20";
    }
  };

  const filteredProofs = proofs.filter(proof =>
    proof.proof_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
    proof.node_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
    proof.merkle_root.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="space-y-6" data-testid="proofs-loading">
        <Skeleton className="h-8 w-48 bg-white/5" />
        <div className="flex gap-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-10 w-24 bg-white/5" />
          ))}
        </div>
        <Skeleton className="h-96 bg-white/5" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="proofs-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl lg:text-3xl font-bold text-white">Proof Explorer</h1>
          <p className="text-gray-500 text-sm mt-1">
            Cryptographic verification logs for all operations
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <Input
              placeholder={t("Search proofs...")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-64 bg-[#0A0A0A] border-white/10 text-white placeholder:text-gray-500"
              data-testid="search-proofs"
            />
          </div>
          <Button 
            variant="outline" 
            size="icon"
            onClick={fetchProofs}
            className="border-white/10 hover:bg-white/5"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#00D2FF]/10 flex items-center justify-center">
                <Zap className="w-5 h-5 text-[#00D2FF]" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white font-mono">
                  {proofs.filter(p => p.proof_type === "inference").length}
                </p>
                <p className="text-xs text-gray-500">Inference Proofs</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#00FF94]/10 flex items-center justify-center">
                <FileCheck className="w-5 h-5 text-[#00FF94]" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white font-mono">
                  {proofs.filter(p => p.proof_type === "action").length}
                </p>
                <p className="text-xs text-gray-500">Action Proofs</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#3399FF]/10 flex items-center justify-center">
                <Shield className="w-5 h-5 text-[#3399FF]" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white font-mono">
                  {proofs.filter(p => p.proof_type === "zk_receipt").length}
                </p>
                <p className="text-xs text-gray-500">ZK Receipts</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#00FF94]/10 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-[#00FF94]" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white font-mono">
                  {proofs.filter(p => p.verified).length}
                </p>
                <p className="text-xs text-gray-500">Verified</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs and table */}
      <Tabs defaultValue="all" value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-[#0A0A0A] border border-white/10">
          <TabsTrigger 
            value="all" 
            className="data-[state=active]:bg-white/10 data-[state=active]:text-white"
          >
            All
          </TabsTrigger>
          <TabsTrigger 
            value="inference" 
            className="data-[state=active]:bg-[#00D2FF]/20 data-[state=active]:text-[#00D2FF]"
          >
            Inference
          </TabsTrigger>
          <TabsTrigger 
            value="action" 
            className="data-[state=active]:bg-[#00FF94]/20 data-[state=active]:text-[#00FF94]"
          >
            Action
          </TabsTrigger>
          <TabsTrigger 
            value="zk_receipt" 
            className="data-[state=active]:bg-[#3399FF]/20 data-[state=active]:text-[#3399FF]"
          >
            ZK Receipts
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-4">
          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="p-0">
              <Table className="data-table">
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="text-gray-500">Status</TableHead>
                    <TableHead className="text-gray-500">Type</TableHead>
                    <TableHead className="text-gray-500">Proof ID</TableHead>
                    <TableHead className="text-gray-500">Node</TableHead>
                    <TableHead className="text-gray-500">Merkle Root</TableHead>
                    <TableHead className="text-gray-500">Timestamp</TableHead>
                    <TableHead className="text-gray-500">TX Hash</TableHead>
                    <TableHead className="text-gray-500"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredProofs.map((proof) => {
                    const TypeIcon = getProofTypeIcon(proof.proof_type);
                    return (
                      <TableRow 
                        key={proof.proof_id}
                        className="border-white/5 hover:bg-white/5 cursor-pointer transition-colors"
                        onClick={() => {
                          setSelectedProof(proof);
                          setDetailOpen(true);
                        }}
                        data-testid={`proof-row-${proof.proof_id}`}
                      >
                        <TableCell>
                          {proof.verified ? (
                            <CheckCircle className="w-4 h-4 text-[#00FF94]" />
                          ) : (
                            <XCircle className="w-4 h-4 text-red-400" />
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge className={getProofTypeColor(proof.proof_type)}>
                            <TypeIcon className="w-3 h-3 mr-1" />
                            {proof.proof_type}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-mono text-[#00D2FF] text-xs">
                          {truncateHash(proof.proof_id)}
                        </TableCell>
                        <TableCell className="font-mono text-gray-400 text-xs">
                          {proof.node_id}
                        </TableCell>
                        <TableCell className="font-mono text-white text-xs">
                          {truncateHash(proof.merkle_root)}
                        </TableCell>
                        <TableCell className="text-gray-400 text-xs">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatDate(proof.timestamp)}
                          </span>
                        </TableCell>
                        <TableCell className="font-mono text-xs">
                          {proof.tx_hash ? (
                            <span className="text-[#00FF94]">{truncateHash(proof.tx_hash, 8)}</span>
                          ) : (
                            <span className="text-gray-600">pending</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <ExternalLink className="w-4 h-4 text-gray-500" />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Proof detail sheet */}
      <Sheet open={detailOpen} onOpenChange={setDetailOpen}>
        <SheetContent className="w-full sm:max-w-lg bg-[#0A0A0A] border-white/10 overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="font-heading text-white flex items-center gap-3">
              {selectedProof?.verified ? (
                <CheckCircle className="w-5 h-5 text-[#00FF94]" />
              ) : (
                <XCircle className="w-5 h-5 text-red-400" />
              )}
              Proof Details
            </SheetTitle>
          </SheetHeader>
          
          {selectedProof && (
            <div className="mt-6 space-y-6">
              {/* Proof type badge */}
              <div className="flex items-center gap-3">
                <Badge className={`${getProofTypeColor(selectedProof.proof_type)} text-sm px-3 py-1`}>
                  {React.createElement(getProofTypeIcon(selectedProof.proof_type), { className: "w-4 h-4 mr-2" })}
                  {selectedProof.proof_type.replace('_', ' ').toUpperCase()}
                </Badge>
                {selectedProof.verified && (
                  <Badge className="bg-[#00FF94]/10 text-[#00FF94] border-[#00FF94]/20">
                    VERIFIED
                  </Badge>
                )}
              </div>

              {/* IDs */}
              <div className="space-y-3">
                <div className="py-2 border-b border-white/5">
                  <span className="text-gray-500 text-sm block mb-1">Proof ID</span>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-[#00D2FF] text-sm break-all">{selectedProof.proof_id}</span>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-6 w-6"
                      onClick={() => handleCopy(selectedProof.proof_id)}
                    >
                      <Copy className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
                <div className="py-2 border-b border-white/5">
                  <span className="text-gray-500 text-sm block mb-1">Node ID</span>
                  <span className="font-mono text-white text-sm">{selectedProof.node_id}</span>
                </div>
                <div className="py-2 border-b border-white/5">
                  <span className="text-gray-500 text-sm block mb-1">Timestamp</span>
                  <span className="text-white text-sm">{formatDate(selectedProof.timestamp)}</span>
                </div>
              </div>

              {/* Cryptographic data */}
              <div>
                <h3 className="font-heading text-white font-semibold mb-3">Cryptographic Data</h3>
                <div className="space-y-3">
                  <div className="glass rounded-lg p-3">
                    <span className="text-gray-500 text-xs block mb-1">Merkle Root</span>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-white text-xs break-all">{selectedProof.merkle_root}</span>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-6 w-6 flex-shrink-0"
                        onClick={() => handleCopy(selectedProof.merkle_root)}
                      >
                        <Copy className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                  <div className="glass rounded-lg p-3">
                    <span className="text-gray-500 text-xs block mb-1">Signature</span>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-white text-xs break-all">{selectedProof.signature}</span>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-6 w-6 flex-shrink-0"
                        onClick={() => handleCopy(selectedProof.signature)}
                      >
                        <Copy className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                  {selectedProof.tx_hash && (
                    <div className="glass rounded-lg p-3">
                      <span className="text-gray-500 text-xs block mb-1">Transaction Hash</span>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-[#00FF94] text-xs break-all">{selectedProof.tx_hash}</span>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-6 w-6 flex-shrink-0"
                          onClick={() => handleCopy(selectedProof.tx_hash)}
                        >
                          <Copy className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Proof data */}
              {selectedProof.data && Object.keys(selectedProof.data).length > 0 && (
                <div>
                  <h3 className="font-heading text-white font-semibold mb-3">Proof Data</h3>
                  <div className="glass rounded-lg p-4 font-mono text-xs space-y-2">
                    {Object.entries(selectedProof.data).map(([key, value]) => (
                      <div key={key} className="flex items-center justify-between">
                        <span className="text-gray-500">{key}</span>
                        <span className="text-white">{String(value)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3">
                <Button 
                  className="flex-1 bg-[#00D2FF] hover:bg-[#00D2FF] text-black font-bold"
                  onClick={() => toast.info("Simulation started")}
                >
                  <Play className="w-4 h-4 mr-2" />
                  Replay Simulation
                </Button>
                {selectedProof.tx_hash && (
                  <Button 
                    variant="outline"
                    className="border-white/10 hover:bg-white/5"
                    onClick={() => window.open(`https://etherscan.io/tx/${selectedProof.tx_hash}`, '_blank')}
                  >
                    <ExternalLink className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default Proofs;
